import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/auth-provider";
import {
  PanelLeftClose,
  PanelLeft,
  LogOut,
} from "lucide-react";
import { SidebarNav } from "./sidebar-nav";
import { SidebarAlerts } from "./sidebar-alerts";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { useLocation } from "@/components/location-provider";
import { useNavigate } from "react-router-dom";
import { VertaliMark, VertaliLogo } from "@/components/logo";

export function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const { current: location } = useLocation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const saved = localStorage.getItem("sidebar-collapsed");
    if (saved === "true") setCollapsed(true);
  }, []);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem("sidebar-collapsed", String(next));
      return next;
    });
  }, []);

  return (
    <aside
      role="navigation"
      aria-label="Main navigation"
      className={cn(
        "hidden lg:flex lg:flex-col bg-sidebar/50 backdrop-blur-2xl border-r border-border/20 transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] relative z-30",
        collapsed ? "w-[64px]" : "w-60"
      )}
    >
      {/* Logo + Toggle */}
      <div
        className={cn(
          "flex items-center flex-shrink-0 relative",
          collapsed ? "h-14 justify-center px-2" : "h-16 justify-between px-4"
        )}
      >
        {collapsed ? (
          <VertaliMark
            size={24}
            className="text-primary drop-shadow-[0_0_8px_var(--color-primary)]"
          />
        ) : (
          <VertaliLogo size={24} className="text-foreground" />
        )}
        <button
          onClick={toggleCollapsed}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          className={cn(
            "rounded-lg p-1.5 text-muted-foreground hover:text-foreground transition-all duration-300",
            collapsed
              ? "absolute -right-3 top-3.5 z-50 bg-popover/80 backdrop-blur-xl border border-border/40 rounded-full shadow-lg"
              : "opacity-0 group-hover/sidebar:opacity-100"
          )}
        >
          {collapsed ? (
            <PanelLeft className="h-3.5 w-3.5" />
          ) : (
            <PanelLeftClose className="h-3.5 w-3.5" />
          )}
        </button>
      </div>

      {/* Divider gradient */}
      <div className="h-px mx-4 bg-gradient-to-r from-border/80 via-border/20 to-transparent" />

      {/* Navigation */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden py-3">
        <SidebarNav collapsed={collapsed} />
        {!collapsed && <SidebarAlerts collapsed={collapsed} />}
      </div>

      {/* User profile footer */}
      <div className="h-px mx-4 bg-gradient-to-r from-transparent via-border/20 to-border/80 flex-shrink-0" />
      <div className={cn(
        "flex-shrink-0",
        collapsed ? "p-2" : "p-3"
      )}>
        {collapsed ? (
          <div className="flex justify-center">
            <Avatar className="h-8 w-8 ring-2 ring-border/30">
              <AvatarFallback className="text-[10px] font-medium">
                {(user?.name || "V").slice(0, 1).toUpperCase()}
              </AvatarFallback>
            </Avatar>
          </div>
        ) : (
          <div className="flex items-center gap-2.5">
            <Avatar className="h-8 w-8 ring-2 ring-border/30">
              <AvatarFallback className="text-[10px] font-medium">
                {(user?.name || "V").slice(0, 1).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold truncate">{user?.name || "Vertali User"}</p>
              <p className="text-[10px] text-muted-foreground/70 truncate">{location.name}</p>
            </div>
            {user ? (
              <button
                onClick={() => { logout(); navigate("/login"); }}
                className="text-muted-foreground hover:text-foreground transition-all p-1.5 rounded-lg hover:bg-muted/40"
                aria-label="Sign out"
              >
                <LogOut className="h-3.5 w-3.5" />
              </button>
            ) : (
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
                <span className="inline-flex h-2 w-2 rounded-full bg-emerald-400" />
              </span>
            )}
          </div>
        )}
      </div>
    </aside>
  );
}
